import { ApprovalsPersons } from "@/components/approvals/ApprovalsPersons.tsx";
import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import { FileItem } from "@/components/files/FileItem.tsx";
import { DeleteListItem } from "@/components/list-item/DeleteListItem.tsx";
import { TimeOffCalendarModal } from "@/components/time-off/widgets/TimeOffCalendarModal.tsx";
import {
  ConfirmationModal,
  Textarea,
  useModals,
  useToasts,
  Modal,
  ModalProps,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSeparator,
  FontAwesomeIcon,
  LinkTo,
  ListItem,
  ListOption,
  Stack,
  Switch,
  Text,
  Title,
} from "@skailer-com/uikit";
import { TimeOffRequestSkeleton } from "@/components/time-off/components/TimeOffRequestSkeleton.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import useTimeOffHook from "@/hooks/use-time-off.hook.tsx";
import { useEmployeeDetails } from "@/queries/employees/use-employees.query.ts";
import { TimeOffBreakdownModal } from "@/pages/time-off/widgets/TimeOffBreakdownModal.tsx";
import {
  useMyTimeOffRequestDetails,
  useTimeOffRequestAnswer,
  useTimeOffRequestCancel,
  useTimeOffRequestDelete,
} from "@/queries/use-time-off-requests.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiApprovalRequestStatus,
  getRequestStatusTitle,
} from "@/types/approvals/approval.types.ts";
import { Rounding } from "@/types/common.types.ts";
import { ApiPermission, EmployeeFieldPermission } from "@/types/role.types.ts";
import {
  ApiTimeOffCategoryType,
  ApiTimeOffPeriodTrackingTime,
} from "@/types/time-off/time-off-policy.types.ts";
import { differenceInCalendarDays } from "date-fns";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";
import { twMerge } from "tailwind-merge";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";

interface TimeOffRequestViewModalProps extends ModalProps {
  requestId: number;
}

export function TimeOffRequestViewModal({ requestId, ...props }: TimeOffRequestViewModalProps) {
  const { renderEmployeeName, renderDate, getDaysToDate, renderShortPeriod } = useFormatter();
  const { getTimeOffRequestStatusColor } = useTimeOffHook();
  const { t } = useTranslation();
  const { id } = props;
  const answerRequest = useTimeOffRequestAnswer();
  const cancelRequest = useTimeOffRequestCancel();
  const deleteTimeOffRequest = useTimeOffRequestDelete();
  const { showInfo } = useToasts();
  const { close, openModal } = useModals();
  const {
    data: requestDetails,
    isLoading: timeOffRequestLoading,
    error: requestDetailsError,
  } = useMyTimeOffRequestDetails(requestId);

  const { canIEdit, canViewField } = usePermissions();
  const canManageRequests = canIEdit(ApiPermission.approve_and_reject_leaves);
  const { data: employee } = useEmployeeDetails(
    requestDetails?.approval_request_details?.employee.id
  );
  const employment = employee?.employee_employment;

  const [isApproveButtonShouldLoading, setIsApprove] = useState<boolean>(false);
  const [useComment, setUseComment] = useState<boolean>(false);
  const [comment, setComment] = useState<string>("");

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.timeOffs.requestViewed, {
      [analyticProperties.id]: requestDetails?.approval_request_details?.id,
      [analyticProperties.title]: "Time Off",
      [analyticProperties.actionType]: "View",
    });
  });

  const isLoadingRequest =
    answerRequest.isPending ||
    cancelRequest.isPending ||
    deleteTimeOffRequest.isPending ||
    timeOffRequestLoading;

  const handleDelete = () => {
    if (isLoadingRequest) return;

    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("core:delete_request_confirm_description"),
      acceptTitle: t("common:yes"),
      onAccept: async function () {
        try {
          if (!requestDetails?.approval_request_details) return;
          await deleteTimeOffRequest.mutateAsync(requestDetails.approval_request_details.id, {
            onSuccess: () => {
              showInfo({ title: t("approval:request_deleted") });

              analyticsService.trackEvent(analyticEvents.approvals.requestAction, {
                [analyticProperties.id]: requestDetails.approval_request_details?.id,
                [analyticProperties.title]: "Time Off",
                [analyticProperties.actionType]: "Delete",
              });

              close(id);
            },
          });
        } catch {
          return;
        }
      },
    });
  };

  const handleAnswerRequest = async (approve: boolean, isBypassApprovalFlow: boolean = false) => {
    if (isLoadingRequest) return;
    if (!requestDetails?.approval_request_details) return;

    setIsApprove(approve);

    const requestId = requestDetails.approval_request_details.id;
    const bypass = canManageRequests ? isBypassApprovalFlow : false;

    const payload = {
      id: requestId,
      approve,
      payload: { is_bypass_approval_flow: bypass, comment: useComment ? comment : null },
    };

    const trackAnalytics = () => {
      analyticsService.trackEvent(analyticEvents.approvals.requestAction, {
        [analyticProperties.id]: requestId,
        [analyticProperties.title]: "Time Off",
        [analyticProperties.actionType]: approve ? "Approve" : "Reject",
      });

      showInfo({
        title: approve ? t("approval:request_approved") : t("approval:request_rejected"),
      });
    };

    if (canManageRequests && isBypassApprovalFlow) {
      openModal(ConfirmationModal, {
        question: t(approve ? "approval:approve_as_admin" : "approval:reject_as_admin"),
        text: t(
          approve ? "approval:approve_as_admin_description" : "approval:reject_as_admin_description"
        ),
        acceptTitle: t(approve ? "approval:approve" : "approval:reject"),
        onAccept: async function () {
          try {
            await answerRequest.mutateAsync(payload);
            trackAnalytics();
          } catch {
            return;
          }
        },
      });
    } else {
      try {
        await answerRequest.mutateAsync(payload);
        trackAnalytics();
      } catch {
        return;
      }
    }
  };

  const handleCancelRequest = () => {
    if (isLoadingRequest) return;
    if (!requestDetails?.approval_request_details?.id) return;
    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("approval:cancel_request_confirm_description"),
      acceptTitle: t("common:yes"),
      onAccept: async function () {
        try {
          if (!requestDetails.approval_request_details) return;
          await cancelRequest.mutateAsync(requestDetails?.approval_request_details.id);

          analyticsService.trackEvent(analyticEvents.approvals.requestAction, {
            [analyticProperties.id]: requestDetails.approval_request_details?.id,
            [analyticProperties.title]: "Time Off",
            [analyticProperties.actionType]: "Cancel",
          });
        } catch {
          return;
        }
      },
    });
  };

  const isHours =
    requestDetails?.time_off_category?.period_track_type === ApiTimeOffPeriodTrackingTime.hours;
  const pluralKey = isHours ? "plurals:plural_hours" : "plurals:plural_day";
  const daysCount =
    requestDetails?.time_off_log?.date_from && requestDetails.time_off_log.date_to
      ? differenceInCalendarDays(
          requestDetails.time_off_log.date_to,
          requestDetails.time_off_log.date_from
        ) + 1
      : 0;
  const inDays = getDaysToDate(requestDetails?.time_off_log?.date_from, Rounding.up);
  const hasUpperMenuOptions =
    requestDetails?.acl.can_edit ||
    requestDetails?.acl.can_cancel ||
    requestDetails?.acl.can_bypass_flow;
  const requestTitle =
    requestDetails?.time_off_category?.title && requestDetails.time_off_log
      ? t("time_off:approve_request_title", {
          request_days: t(pluralKey, {
            count: Math.abs(parseFloat(requestDetails.time_off_log.balance)),
          }),
          category_title: requestDetails.time_off_category.title,
        })
      : "";

  useEffect(() => {
    if (requestDetailsError) {
      close(id);
    }
  }, [requestDetailsError]);

  return (
    <Modal
      title={
        timeOffRequestLoading
          ? t("common:loading")
          : requestDetails?.time_off_category?.title
            ? t("time_off:category_request", {
                category: requestDetails.time_off_category.title,
              })
            : ""
      }
      closeByOutsidePress={
        requestDetails?.approval_request_details?.status !== ApiApprovalRequestStatus.awaiting
      }
      {...props}
      size={"lg"}
      headerActionsSlot={(() => {
        const canBypassFlow = canManageRequests && requestDetails?.acl.can_bypass_flow;

        const hasDeleteOption = requestDetails?.acl.can_delete;

        const shouldShowMenu = hasUpperMenuOptions || hasDeleteOption;

        const shouldShowDivider = hasUpperMenuOptions && hasDeleteOption;

        return shouldShowMenu ? (
          <DropdownMenu
            trigger={
              <Button
                variant={"secondary"}
                rightIcon={<FontAwesomeIcon icon={"fa-light fa-chevron-down"} />}
              >
                {t("common:actions")}
              </Button>
            }
          >
            {requestDetails.acl.can_edit && (
              <LinkTo
                to={`/time-off/request/${requestDetails.approval_request_details?.id}/edit`}
                target={"_blank"}
                className={"no-underline"}
              >
                <ListOption title={t("core:edit_request")} />
              </LinkTo>
            )}
            {requestDetails.acl.can_cancel && (
              <ListOption
                title={t("approval:cancel_request") + "..."}
                disabled={isLoadingRequest}
                onClick={() => handleCancelRequest()}
              />
            )}
            {canBypassFlow && (
              <DropdownMenuItem
                title={t("approval:approve_as_admin") + "..."}
                description={t("approval:approve_as_admin_description")}
                leftIcon={<FontAwesomeIcon icon="fa-light fa-crown text-warning" />}
                isSelected={false}
                disabled={isLoadingRequest}
                onClick={() => handleAnswerRequest(true, true)}
              />
            )}
            {canBypassFlow && (
              <DropdownMenuItem
                title={t("approval:reject_as_admin") + "..."}
                description={t("approval:reject_as_admin_description")}
                leftIcon={<FontAwesomeIcon icon="fa-light fa-crown text-warning" />}
                isSelected={false}
                disabled={isLoadingRequest}
                onClick={() => handleAnswerRequest(false, true)}
              />
            )}
            {shouldShowDivider && <DropdownMenuSeparator />}
            {hasDeleteOption && (
              <ListOption
                titleSlot={
                  <Text className={"text-danger"}>{t("approval:delete_request") + "..."}</Text>
                }
                onClick={() => handleDelete()}
              />
            )}
          </DropdownMenu>
        ) : (
          <></>
        );
      })()}
      actions={
        requestDetails?.acl.can_reject || requestDetails?.acl.can_approve ? (
          <Stack gap={"smd"} className={"pt-1"}>
            <Stack gap={"sm"}>
              <Switch
                size={"sm"}
                checked={useComment}
                onChange={setUseComment}
                title={t("common:add_comment")}
              />
              {useComment && (
                <Textarea
                  label={t("approval:comment_your_decision") + "..."}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              )}
            </Stack>
            <Stack gap={"sm"} justify={"between"}>
              {requestDetails?.acl.can_approve && (
                <Button
                  type={"submit"}
                  size={"lg"}
                  className={"w-full"}
                  disabled={isLoadingRequest}
                  isLoading={answerRequest.isPending && isApproveButtonShouldLoading}
                  onClick={() => {
                    void handleAnswerRequest(true);
                  }}
                >
                  {t("approval:approve")}
                </Button>
              )}
              {requestDetails.acl.can_reject && (
                <Button
                  variant={"secondary"}
                  type={"submit"}
                  size={"lg"}
                  className={"w-full"}
                  disabled={isLoadingRequest}
                  isLoading={answerRequest.isPending && !isApproveButtonShouldLoading}
                  onClick={() => {
                    void handleAnswerRequest(false);
                  }}
                >
                  {t("approval:reject")}
                </Button>
              )}
            </Stack>
          </Stack>
        ) : (
          <Button
            size={"lg"}
            className={"w-full"}
            variant={"secondary"}
            onClick={() => {
              close(id);
            }}
          >
            {t("common:close")}
          </Button>
        )
      }
      withCloser={true}
    >
      <>
        {timeOffRequestLoading && <TimeOffRequestSkeleton />}
        {requestDetails?.approval_request_details && (
          <Stack>
            <Stack gap={"md"}>
              <EmployeeAvatar
                employee={requestDetails.approval_request_details.employee}
                size={"2xl"}
              />

              <Stack>
                {getEmployeePositionTitle(requestDetails.approval_request_details.employee) ? (
                  <Text className={"text-lg"}>
                    {getEmployeePositionTitle(requestDetails.approval_request_details.employee)}
                  </Text>
                ) : (
                  ""
                )}

                <Title
                  header={renderEmployeeName(requestDetails.approval_request_details.employee)}
                  size={"lg"}
                  caption={requestTitle}
                />
              </Stack>
            </Stack>
            <Title header={t("common:details")} size={"xs"} paddingBottom paddingTop />
            <Stack>
              <ListItem
                preTitle={t("common:status")}
                titleSlot={
                  <Text
                    className={twMerge(
                      getTimeOffRequestStatusColor(requestDetails.approval_request_details.status)
                        .textColor,
                      "font-semibold"
                    )}
                  >
                    {getRequestStatusTitle(requestDetails.approval_request_details.status)}
                  </Text>
                }
              />
              <ListItem
                preTitle={t("common:type")}
                title={requestDetails.time_off_category?.title ?? ""}
              />
              {employment?.legal_entity &&
                canViewField(
                  employee,
                  EmployeeFieldPermission.employee_employment_legal_entity
                ) && (
                  <ListItem
                    preTitle={t("core:legal_entity")}
                    title={employment.legal_entity.title}
                  />
                )}
              {employment?.employment_type &&
                canViewField(
                  employee,
                  EmployeeFieldPermission.employee_employment_employment_type
                ) && (
                  <ListItem
                    preTitle={t("core:employment_type")}
                    title={employment.employment_type.title}
                  />
                )}
              {requestDetails.time_off_log.date_from && (
                <ListItem
                  preTitle={t("common:dates")}
                  title={
                    requestDetails.time_off_log.date_to
                      ? renderShortPeriod(
                          requestDetails.time_off_log.date_from,
                          requestDetails.time_off_log.date_to
                        )
                      : renderDate(requestDetails.time_off_log.date_from, "PP EEEE")
                  }
                  caption={
                    inDays
                      ? t("common:in_days", { days: t("plurals:plural_day", { count: inDays }) })
                      : ""
                  }
                  valueSlot={
                    hasUpperMenuOptions && requestDetails.approval_request_details ? (
                      <Button
                        size={"sm"}
                        variant={"secondary"}
                        onClick={() =>
                          openModal(TimeOffCalendarModal, {
                            employeeId: requestDetails.approval_request_details!.employee.id,
                          })
                        }
                      >
                        {t("core:view_on_calendar")}
                      </Button>
                    ) : (
                      ""
                    )
                  }
                />
              )}

              <ListItem
                preTitle={t("time_off:balance")}
                title={
                  requestDetails.time_off_log
                    ? t("time_off:count_days_requested", {
                        count_days:
                          t(pluralKey, {
                            count: -parseFloat(requestDetails.time_off_log.balance),
                          }) +
                          (isHours
                            ? ` (${t("time_off:in_units")} ${t("plurals:plural_day", {
                                count: daysCount,
                              })})`
                            : ""),
                      })
                    : "-"
                }
                captionSlot={
                  <Stack>
                    {requestDetails.time_off_category?.category_type ===
                      ApiTimeOffCategoryType.accrues_time && (
                      <Text>
                        {t("time_off:count_days_available", {
                          count_days: t("plurals:plural_day", {
                            count: Math.abs(
                              parseFloat(
                                requestDetails.time_off_category?.balance.available_balance ?? "0"
                              )
                            ),
                          }),
                        })}
                      </Text>
                    )}
                    {requestDetails.time_off_log.breakdown?.is_manual_modified && (
                      <Text className={"text-secondary text-warning"}>
                        {t("time_off:modified_breakdown")}
                      </Text>
                    )}
                  </Stack>
                }
                valueSlot={
                  requestDetails.time_off_log.breakdown ? (
                    <Button
                      variant="secondary"
                      size={"sm"}
                      onClick={() => {
                        openModal(TimeOffBreakdownModal, {
                          breakdown: requestDetails.time_off_log.breakdown!,
                          available_balance: 0,
                          period_track_type: requestDetails.time_off_category?.period_track_type,
                          viewMode: true,
                        });
                      }}
                    >
                      {t("time_off:view_breakdown")}
                    </Button>
                  ) : undefined
                }
              />
              {requestDetails.approval_request_details.created_by &&
                requestDetails.approval_request_details.created_by.id !=
                  requestDetails.approval_request_details.employee.id && (
                  <ListItem
                    preTitle={t("common:created_by")}
                    title={renderEmployeeName(requestDetails.approval_request_details.created_by)}
                  />
                )}

              {requestDetails.time_off_log.comment && (
                <ListItem
                  preTitle={t("common:comments")}
                  title={requestDetails.time_off_log.comment}
                />
              )}

              {requestDetails.time_off_log.files.length > 0 && (
                <Stack className={"pt-4 pb-2"} gap={"sm"}>
                  {requestDetails.time_off_log.files.map((file) => (
                    <FileItem key={`file${file.id}`} file={file} />
                  ))}
                </Stack>
              )}
            </Stack>

            <ApprovalsPersons request={requestDetails.approval_request_details} />
            {requestDetails.acl.can_delete && (
              <Stack>
                <Title
                  header={t("common:actions")}
                  size="xs"
                  paddingTop
                  paddingBottom={false}
                  className={"-mb-2"}
                />
                <DeleteListItem
                  title={t("time_off:time_off_delete_title", {
                    time_off_type: requestDetails.time_off_category?.title?.toLowerCase(),
                  })}
                  onDelete={handleDelete}
                />
              </Stack>
            )}
          </Stack>
        )}
      </>
    </Modal>
  );
}
